/**
 * Modal de colaborador — cadastrar (POST) e corrigir cadastro (PATCH).
 *
 * Selects em cascata empresa > unidade (obra) > setor > função; a API revalida
 * a cadeia (422 se incoerente). CPF aceita pontuação e envia só dígitos.
 *
 * No modo correção:
 *  - a EMPRESA não muda (trocar invalidaria a cadeia e o histórico — nesse caso
 *    o certo é inativar e recadastrar);
 *  - só os campos realmente alterados vão no PATCH. Isso não é economia: mandar
 *    obra/setor/função sem necessidade dispara a checagem de atendimento em
 *    aberto e barraria até uma correção de nome.
 */
import { useState } from "react";

import {
  useAtualizarColaborador,
  useCriarColaborador,
  useEmpresas,
  useFuncoes,
  useSetores,
  useUnidades,
} from "@/hooks/useApi";
import type { Colaborador } from "@/types/api";

interface Props {
  /** Ausente = cadastrar; presente = corrigir o cadastro deste colaborador. */
  colaborador?: Colaborador;
  onSaved: (c: Colaborador, editado: boolean) => void;
  onClose: () => void;
  /** Inativa o cadastro atual. O aviso sobre empresa manda fazer isso quando o
   *  colaborador foi cadastrado na empresa errada — sem o botão aqui, a
   *  instrução mandaria a atendente procurar uma ação em outra tela. */
  onInativar?: (c: Colaborador) => void;
}

export function ColaboradorFormModal({ colaborador, onSaved, onClose, onInativar }: Props) {
  const editando = colaborador !== undefined;
  const criar = useCriarColaborador();
  const atualizar = useAtualizarColaborador();

  const [empresaId, setEmpresaId] = useState<number | null>(colaborador?.empresa_id ?? null);
  const [unidadeId, setUnidadeId] = useState<number | null>(colaborador?.unidade_id ?? null);
  const [setorId, setSetorId] = useState<number | null>(colaborador?.setor_id ?? null);
  const [funcaoId, setFuncaoId] = useState<number | null>(colaborador?.funcao_id ?? null);
  const [nome, setNome] = useState(colaborador?.nome ?? "");
  const [cpf, setCpf] = useState(colaborador?.cpf ?? "");
  const [rg, setRg] = useState(colaborador?.rg ?? "");
  const [nascimento, setNascimento] = useState(colaborador?.data_nascimento ?? "");
  const [sexo, setSexo] = useState<"" | "M" | "F">(colaborador?.sexo ?? "");

  const { data: empresas } = useEmpresas();
  const { data: unidades } = useUnidades(empresaId);
  const { data: setores } = useSetores(unidadeId);
  const { data: funcoes } = useFuncoes(setorId);

  const salvando = criar.isPending || atualizar.isPending;
  const erro = (criar.error ?? atualizar.error) as Error | undefined;

  const cpfDigitos = cpf.replace(/\D/g, "");
  const ok =
    nome.trim().length >= 2 &&
    cpfDigitos.length === 11 &&
    rg.trim().length >= 3 &&   // RG é obrigatório: vai impresso no ASO
    empresaId !== null && unidadeId !== null && setorId !== null && funcaoId !== null;

  /** Só o que mudou em relação ao cadastro atual (ver comentário do topo). */
  function alteracoes(): Partial<Colaborador> {
    const atual = colaborador!;
    const novo = {
      unidade_id: unidadeId!,
      setor_id: setorId!,
      funcao_id: funcaoId!,
      nome: nome.trim(),
      cpf: cpfDigitos,
      rg: rg.trim(),
      data_nascimento: nascimento || null,
      sexo: sexo || null,
    };
    const antes = atual as unknown as Record<string, unknown>;
    return Object.fromEntries(Object.entries(novo).filter(([k, v]) => v !== antes[k]));
  }

  async function salvar() {
    if (!ok) return;
    try {
      if (editando) {
        const patch = alteracoes();
        if (Object.keys(patch).length === 0) { onClose(); return; }
        onSaved(await atualizar.mutateAsync({ id: colaborador.id, patch }), true);
      } else {
        onSaved(await criar.mutateAsync({
          empresa_id: empresaId!,
          unidade_id: unidadeId!,
          setor_id: setorId!,
          funcao_id: funcaoId!,
          nome: nome.trim(),
          cpf: cpfDigitos,
          rg: rg.trim(),
          data_nascimento: nascimento || null,
          sexo: sexo || null,
        }), false);
      }
    } catch { /* erro exibido abaixo */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">
            {editando ? "Corrigir cadastro" : "Cadastrar colaborador"}
          </h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Nome completo</label>
          <input className="input mt-1" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">CPF</label>
            <input
              className="input mt-1" inputMode="numeric" placeholder="000.000.000-00"
              value={cpf} onChange={(e) => setCpf(e.target.value)}
            />
            {cpf && cpfDigitos.length !== 11 ? (
              <p className="mt-0.5 text-xs text-anarosa">CPF precisa de 11 dígitos ({cpfDigitos.length}/11)</p>
            ) : null}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">RG</label>
            <input
              className="input mt-1" value={rg} onChange={(e) => setRg(e.target.value)}
              placeholder="obrigatório"
            />
            {rg && rg.trim().length < 3 ? (
              <p className="mt-0.5 text-xs text-anarosa">informe o RG completo</p>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Nascimento (opc.)</label>
            <input className="input mt-1" type="date" value={nascimento} onChange={(e) => setNascimento(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Sexo (opc.)</label>
            <select className="input mt-1" value={sexo} onChange={(e) => setSexo(e.target.value as "" | "M" | "F")}>
              <option value="">—</option>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Empresa</label>
          <select
            className="input mt-1"
            value={empresaId ?? ""}
            disabled={editando}
            onChange={(e) => {
              setEmpresaId(e.target.value ? Number(e.target.value) : null);
              setUnidadeId(null); setSetorId(null); setFuncaoId(null);
            }}
          >
            <option value="">Selecione…</option>
            {(empresas ?? []).map((em) => (
              <option key={em.id} value={em.id}>{em.razao_social}</option>
            ))}
          </select>
          {editando ? (
            <p className="mt-0.5 text-xs text-ink-muted">
              A empresa não muda aqui — trocá-la invalidaria obra, setor, função e o
              histórico de atendimentos. Se o colaborador foi cadastrado na empresa
              errada,{" "}
              {onInativar && colaborador?.ativo ? (
                <button type="button" className="text-anarosa underline"
                  onClick={() => onInativar(colaborador)}>
                  inative este cadastro
                </button>
              ) : "inative este cadastro"}{" "}
              e crie outro na empresa certa.
            </p>
          ) : null}
        </div>

        {/* Obra escolhida sem setor: acontece quando a leitura do PCMSO não
            identificou a obra de cada função e todas foram para outra obra.
            Sem este aviso o campo Setor fica mudo e ninguém entende por quê. */}
        {unidadeId !== null && setores !== undefined && setores.length === 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Esta obra <strong>não tem setor cadastrado</strong>, por isso a lista de
            setores está vazia. Isso costuma acontecer quando o PCMSO não indicou a
            obra de cada função.
            <span className="mt-1 block text-xs">
              Não escolha outra obra só para achar a função — o ASO sairia com os
              riscos e exames do contrato errado. Cadastre o setor e a função desta
              obra na página da empresa, ou chame o suporte.
            </span>
          </div>
        )}

        {empresaId !== null && unidades !== undefined && unidades.length === 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Esta empresa ainda <strong>não tem PCMSO aprovado</strong>, então não há
            unidades, setores nem funções para vincular. Aprove o PCMSO em{" "}
            <strong>Documentos (PCMSO/PGR)</strong> — a aprovação cria a estrutura
            automaticamente — ou cadastre unidade/setor/função na página da empresa.
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Obra / Unidade</label>
            <select
              className="input mt-1"
              value={unidadeId ?? ""}
              disabled={empresaId === null}
              onChange={(e) => {
                setUnidadeId(e.target.value ? Number(e.target.value) : null);
                setSetorId(null); setFuncaoId(null);
              }}
            >
              <option value="">—</option>
              {(unidades ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Setor</label>
            <select
              className="input mt-1"
              value={setorId ?? ""}
              disabled={unidadeId === null}
              onChange={(e) => {
                setSetorId(e.target.value ? Number(e.target.value) : null);
                setFuncaoId(null);
              }}
            >
              <option value="">—</option>
              {(setores ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Função</label>
            <select
              className="input mt-1"
              value={funcaoId ?? ""}
              disabled={setorId === null}
              onChange={(e) => setFuncaoId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">—</option>
              {(funcoes ?? []).map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || salvando} onClick={salvar}>
            {salvando ? "Salvando…" : editando ? "Salvar correção" : "Cadastrar"}
          </button>
        </div>
        {erro ? <p className="text-sm text-anarosa">{erro.message}</p> : null}
      </div>
    </div>
  );
}
